import { useMemo, useState } from "react";

import { deleteJson, fetchJson, postJson, putJson } from "utils/fetch";
import handleUserPromise from "utils/handle-user-promise";

type LoadingStyle = "leading" | "trailing" | "leadingTrailing";

type Options = {
  loadingStyle?: LoadingStyle;
};

type ReturnsPromise = <T>(...args: never[]) => Promise<T>;

/*
 * Combines making a request with "loading" state management, whilst also nicely
 * handling promise failures by showing an error modal to the user (if used when
 * mounted underneath something which can show these modals).
 *
 * The `loadingStyle` determines when you want this hook to update the loading state
 * on your behalf. Use `leading` if you only want to be notified when loading starts,
 * use `trailing` if you only want to be notified when loading ends and `leadingTrailing`
 * for both. For example, `leading` is useful if on success you will redirect away, so
 * keeping the loading spinner active during that redirect is a better user experience.
 */
const createRequest =
  <U extends ReturnsPromise>(
    method: U,
    loadingHandler: (loading: boolean) => void,
    loadingStyle: LoadingStyle,
  ) =>
  <T>(...args: Parameters<U>) => {
    const promise = method<T>(...args);

    return handleUserPromise<T>(promise, {
      [loadingStyle]: loadingHandler,
    });
  };

const useFetchJson = ({ loadingStyle = "leadingTrailing" }: Options = {}) => {
  const [loading, setLoading] = useState(false);

  const makeRequest = useMemo(
    () => createRequest(fetchJson, setLoading, loadingStyle),
    [loadingStyle],
  );

  return [makeRequest, loading, setLoading] as const;
};

const usePostJson = ({ loadingStyle = "leadingTrailing" }: Options = {}) => {
  const [loading, setLoading] = useState(false);

  const makeRequest = useMemo(
    () => createRequest(postJson, setLoading, loadingStyle),
    [loadingStyle],
  );

  return [makeRequest, loading, setLoading] as const;
};

const usePutJson = ({ loadingStyle = "leadingTrailing" }: Options = {}) => {
  const [loading, setLoading] = useState(false);

  const makeRequest = useMemo(
    () => createRequest(putJson, setLoading, loadingStyle),
    [loadingStyle],
  );

  return [makeRequest, loading, setLoading] as const;
};

const useDeleteJson = ({ loadingStyle = "leadingTrailing" }: Options = {}) => {
  const [loading, setLoading] = useState(false);

  const makeRequest = useMemo(
    () => createRequest(deleteJson, setLoading, loadingStyle),
    [loadingStyle],
  );

  return [makeRequest, loading, setLoading] as const;
};

export { createRequest, useDeleteJson, useFetchJson, usePostJson, usePutJson };
